import { applyPositionSuggestion, same } from '@/custom/classes/position_assistant'

export default {
  data () {
    return { assistantSession: this.payload.assistant_session || null, assistantReady: false, assistantLoadError: '', assistantJobTitles: null, assistantSkillsRevision: 0, assistantTouched: {}, assistantWorkingFields: [], assistantWorkingArea: null }
  },
  computed: {
    assistantDraft () {
      if (!this.form) return {}
      const fields = this.form.data.position.fields
      const draft = {}
      for (const key of ['title', 'priority_warning', 'abstract', 'owner', 'company_position_id', 'message', 'salary_expectations_comment']) draft[key] = fields[key].value || ''
      for (const key of ['min_seniority', 'max_seniority', 'max_salary_expectations']) {
        const value = fields[key].value
        draft[key] = value === '' || value == null || !Number.isFinite(Number(value)) ? null : Number(value)
      }
      draft.is_discrete = fields.is_discrete.value === 'true' ? true : fields.is_discrete.value === 'false' ? false : null
      draft.work_entails = this.workEntails.split('@@@').filter(line => line.trim()).join('\n')
      draft.contact_email = this.contactEmail
      draft.contact_phones = this.contactPhones
      draft.location_description = (this.location && this.location.description) || ''
      draft.skill_ids = [...new Set((this.positionData.positions_skills || []).map(item => item.skill_id || item.skill.id))]
      draft.job_title_ids = (this.assistantJobTitles || []).map(item => item.id)
      return draft
    },
    assistantModalCount () { return this.assistantSession ? this.$store.state.modals.modals.length : 0 }
  },
  watch: {
    assistantModalCount (count, before) {
      if (this.assistantSession && this.assistantReady && count < before) {
        this.refreshAssistantSkills().catch(() => { this.assistantLoadError = 'רענון הכישורים נכשל. יש לפתוח מחדש את פרטי המשרה לפני המשך המילוי בעזרת הבוט.' })
      }
    },
    // A field the bot is working on (request + save) is locked for editing.
    assistantWorkingFields (fields) {
      if (this._releaseWorkingLock) this._releaseWorkingLock()
      this._releaseWorkingLock = this.lockAssistantFields(fields.filter(key => !this.assistantTouched[key]))
    },
    // The form area the bot is filling is marked pink (.ai-field-working).
    assistantWorkingArea (area, before) {
      for (const [id, add] of [[before, false], [area, true]]) {
        const box = id && this.assistantFieldElements([id])[0]
        if (box) box.classList.toggle('ai-field-working', add)
      }
    }
  },
  beforeDestroy () {
    if (this._releaseWorkingLock) this._releaseWorkingLock()
    if (this.assistantSession) this.$store.commit('modals/clearDirtyGuard', this.payload.modal_id)
  },
  methods: {
    assistantFieldElements (keys) {
      return keys.map(field => {
        const ref = this.$refs['assistant-' + field]
        return ref && (ref.$el || ref)
      }).filter(Boolean)
    },
    // Ref-counted inert/disabled lock, so overlapping locks (working + saving)
    // restore each control's original state only when the last one releases.
    lockAssistantFields (keys) {
      const elements = this.assistantFieldElements(keys)
      const inputs = elements.flatMap(element => element.matches('input,textarea,select,button') ? [element] : [...element.querySelectorAll('input,textarea,select,button')])
      const locks = this._assistantLocks || (this._assistantLocks = { inert: new Map(), disabled: new Map() })
      const targets = [...elements.map(node => ['inert', node]), ...inputs.map(node => ['disabled', node])]
      for (const [prop, node] of targets) {
        const lock = locks[prop].get(node) || { count: 0, previous: node[prop] }
        lock.count++
        locks[prop].set(node, lock)
        node[prop] = true
      }
      let released = false
      return () => {
        if (released) return
        released = true
        for (const [prop, node] of targets) {
          const lock = locks[prop].get(node)
          if (lock && --lock.count === 0) {
            node[prop] = lock.previous
            locks[prop].delete(node)
          }
        }
      }
    },
    markAssistantEdit (event) {
      for (const key of Object.keys(this.$refs).filter(key => key.startsWith('assistant-'))) {
        const ref = this.$refs[key]
        const element = ref && (ref.$el || ref)
        if (element && (element === event.target || element.contains(event.target))) this.$set(this.assistantTouched, key.slice(10), true)
      }
    },
    async refreshAssistantSkills () {
      const position = await this.$network_utils.getPosition({ id: this.positionData.id })
      this.$set(this.positionData, 'positions_skills', position.positions_skills)
      this.assistantSkillsRevision++
    },
    async applyAssistantSuggestion (key, value, snapshot) {
      if (this._isDestroyed) return false
      const modals = this.$store.state.modals.modals
      if (modals.length && modals[modals.length - 1].modal_id !== this.payload.modal_id) throw new Error('Another editor is open')
      const keys = key === 'seniority_range' ? ['min_seniority', 'max_seniority'] : [key]
      if (keys.some(field => this.assistantTouched[field])) return false
      if (snapshot && keys.some(field => !same(snapshot[field], this.assistantDraft[field]))) return false
      const elements = this.assistantFieldElements(keys)
      if (elements.some(element => element === document.activeElement || element.contains(document.activeElement))) return false
      if (key === 'seniority_range') {
        const min = value.min_seniority == null ? this.assistantDraft.min_seniority : value.min_seniority
        const max = value.max_seniority == null ? this.assistantDraft.max_seniority : value.max_seniority
        if (min != null && max != null && min > max) throw new Error('Invalid seniority range')
      }
      const release = this.lockAssistantFields(keys)
      try {
        const response = await applyPositionSuggestion(this.$network_utils, this.positionData.id, this.companyId, key, value)
        if (this._isDestroyed) return false
        if (key === 'skill_ids') await this.refreshAssistantSkills()
        else if (key === 'job_title_ids') {
          const editor = this.$refs['assistant-job_title_ids']
          const saved = response
          const existingIds = editor.selectedJobTitles.map(item => item.id)
          saved.forEach(item => { if (!existingIds.includes(item.job_title.id)) editor.selectedJobTitles.push(editor.transformJobTitles(item)) })
        } else if (key === 'seniority_range') {
          for (const field of keys) this.form.data.position.fields[field].value = response[field] == null ? '' : response[field]
          this.initMaxSeniorityCheck(response)
        } else if (key === 'work_entails') this.workEntails = value.split(/\r?\n/).filter(line => line.trim()).join('@@@')
        else if (key === 'contact_email') this.contactEmail = value
        else if (key === 'contact_phones') this.contactPhones = value
        else this.form.data.position.fields[key].value = key === 'is_discrete' ? String(value) : value
        await this.$nextTick()
      } finally { release() }
    }
  }
}
